/**
 * Authentication configuration for the web client.
 *
 * Mirrors the API's `Authentication:Mode` switch. In `development` the app sends the simulated
 * identity headers the API's development scheme reads, so the whole stack is exercisable before an
 * Entra External ID tenant exists. Any other value uses real MSAL sign-in.
 */

/** How the web client authenticates. */
export type AuthMode = 'development' | 'entraExternalId';

/** Resolved authentication settings. */
export interface AuthConfig {
  readonly mode: AuthMode;
  /**
   * Simulated identity used only when {@link mode} is `development`. The optional `role` maps to the
   * API's `X-Dev-Role` header, so a developer can exercise the organiser and administrator paths
   * (moderation, event photo management) by setting `VITE_DEV_ROLE=administrator`.
   */
  readonly developmentIdentity: {
    readonly subject: string;
    readonly name: string;
    readonly role?: string;
  };
  /** MSAL settings, populated only when {@link mode} is `entraExternalId`. */
  readonly msal: { readonly clientId: string; readonly authority: string; readonly apiScope: string };
}

function env(key: string): string {
  return (import.meta.env[key] as string | undefined) ?? '';
}

/**
 * Reads authentication settings from Vite environment variables.
 *
 * @returns The resolved configuration.
 */
export function readAuthConfig(): AuthConfig {
  const mode: AuthMode = env('VITE_AUTH_MODE') === 'development' ? 'development' : 'entraExternalId';

  return {
    mode,
    developmentIdentity: {
      subject: env('VITE_DEV_SUBJECT') || 'web-dev-user',
      name: env('VITE_DEV_NAME') || 'Web Dev User',
      ...(env('VITE_DEV_ROLE') === '' ? {} : { role: env('VITE_DEV_ROLE') }),
    },
    msal: {
      clientId: env('VITE_ENTRA_CLIENT_ID'),
      authority: env('VITE_ENTRA_AUTHORITY'),
      apiScope: env('VITE_ENTRA_API_SCOPE'),
    },
  };
}
